/**
 * Spec 200 — pairing route. The `app.solvr.es` landing surface for
 * an unpaired browser.
 *
 * Lifecycle:
 *   generating → waiting → paired (→ navigates away)
 *                       ↘ expired (→ regenerate automatically)
 *                       ↘ error (→ shows retry)
 *
 * Implementation steps map 1:1 to spec 200 §4.3:
 *
 *   1. Generate a 16-byte pairingId (`crypto.getRandomValues` →
 *      hex). The first 6 hex chars uppercased are the anti-phishing
 *      verification code shown ALSO on mobile after the scan, so
 *      the user can compare by eye before tapping Confirm.
 *   2. Generate an ephemeral P-256 keypair via webCrypto.ts; export
 *      the public key as 65-byte SEC1-uncompressed (matches the
 *      shape mobile produces with quickCrypto).
 *   3. Build the QR URI `cuadrilla-pair://<pairingId>?webPub=<base64>`.
 *      The base64 webPub is URL-encoded once so the URI parses on
 *      both sides without further escaping (mobile decodeURIComponent
 *      mirrors the encoding).
 *   4. Poll GET /pair-sessions/:id/poll every 2 s. On 200 with the
 *      ciphertext bundle: ECDH(web.priv, mobile.pub) → HKDF →
 *      AES-GCM-256 decrypt → JSON parse → `saveIdentity(...)`.
 *   5. DELETE /pair-sessions/:id (idempotent, single-use).
 *   6. Register THIS browser in /web-terminals so the mobile-side
 *      Settings → "Sesiones conectadas" listing shows it. The web
 *      generates its own terminalId locally; the relay row is the
 *      authoritative copy.
 *
 * 5-minute TTL is the only deadline that matters: we generate a
 * fresh pairingId+keypair as soon as the relay returns
 * `{ expired: true }` so the user never sees a permanently dead
 * QR. A regen is a free retry — there's no rate limit on the
 * unauthenticated /poll endpoint and the web has no reason to
 * conserve pairingIds.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import QRCode from 'qrcode';
import { Info, Monitor, RefreshCw, ShieldCheck, Smartphone } from 'lucide-react';

import { BrandMark } from '../components/BrandMark';
import { RELAY_HTTP_URL } from '../config/env';
import { aesGcmDecrypt, bytesToUtf8, ecdhP256, hkdfSha256, utf8ToBytes } from '../crypto/webCrypto';
import { saveIdentity, type WebIdentity } from '../store/keys';

import './Pair.css';

const PAIRING_INFO = 'cuadrilla-pair-v1';
const POLL_INTERVAL_MS = 2_000;
// The relay enforces a 5-minute TTL on the pair_sessions row. We
// regen our local pairingId on the same cadence so the QR shown in
// the UI stays valid: 4m 50s gives us 10s of slack so a polling
// round-trip in flight when we're about to regen still completes
// before the row vanishes.
const PAIRING_TTL_MS = 4 * 60 * 1000 + 50 * 1000;

type Status = 'generating' | 'waiting' | 'paired' | 'expired' | 'error';

interface PairingState {
  pairingId: string;
  webPubBytes: Uint8Array;
  webPrivKey: CryptoKey;
  qrDataUrl: string;
  pairUri: string;
  generatedAt: number;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0');
  return out;
}
function bytesToBase64(bytes: Uint8Array): string {
  // btoa wants a binary string. TextDecoder('latin1') is the cleanest
  // path that doesn't allocate a temp Buffer (no Node fallback in browser).
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

interface PairProps {
  /** Called after the identity has been persisted. Caller redirects
   *  to /works (or wherever the post-pair landing surface is). The
   *  Pair component itself doesn't navigate — keeps the route
   *  decoupled from the router we ship in Fase 4. */
  onPaired: (identity: WebIdentity) => void;
}

export function Pair({ onPaired }: PairProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>('generating');
  const [pairing, setPairing] = useState<PairingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [regenTrigger, setRegenTrigger] = useState(0);

  // Generation effect — runs on mount AND whenever we need to
  // regenerate (TTL expired, user-triggered retry).
  useEffect(() => {
    let cancelled = false;
    setStatus('generating');
    setError(null);
    (async () => {
      try {
        const pairingIdBytes = crypto.getRandomValues(new Uint8Array(16));
        const pairingId = bytesToHex(pairingIdBytes);
        const keypair = await ecdhP256.generateKeyPair();
        const webPubBytes = await ecdhP256.exportPublicKey(keypair.publicKey);
        const pairUri = `cuadrilla-pair://${pairingId}?webPub=${encodeURIComponent(bytesToBase64(webPubBytes))}`;
        // qrcode lib produces a data: URL with a PNG that scales
        // crisply via CSS. SVG would also be fine; PNG keeps the
        // bundle smaller (no client-side SVG renderer needed).
        const qrDataUrl = await QRCode.toDataURL(pairUri, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 320,
        });
        if (cancelled) return;
        setPairing({
          pairingId,
          webPubBytes,
          webPrivKey: keypair.privateKey,
          qrDataUrl,
          pairUri,
          generatedAt: Date.now(),
        });
        // Dev bridge: publish the current pair URI so the local CLI
        // can grab it without sharing localStorage. Skip in production
        // and under Vitest (which also sets DEV=true) so the phantom
        // request doesn't drift fetch-counting assertions.
        if (import.meta.env.DEV && !import.meta.env.VITEST) {
          try { void fetch('/__dev/pair-uri', { method: 'POST', body: pairUri }); } catch { /* ignore */ }
        }
        setStatus('waiting');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to generate pairing QR');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [regenTrigger]);

  // Polling + auto-regen effect — runs while status === 'waiting'.
  useEffect(() => {
    if (status !== 'waiting' || !pairing) return;
    const ac = new AbortController();
    // `unmounted` is the cleanup signal — set when the cleanup
    // callback runs (status change, component unmount). Distinct
    // from `ac.signal.aborted` because we also call `ac.abort()`
    // from the success path to stop polling, and we don't want
    // that to look like an unmount.
    let unmounted = false;

    async function pollOnce() {
      if (unmounted) return;

      // Stage 1 — network round-trip. Any failure here (offline,
      // DNS hiccup, abort, malformed JSON) is treated as transient:
      // silent return, the next interval tick retries. Keeps the
      // user looking at the QR through brief connection drops.
      let res: Response;
      try {
        res = await fetch(`${RELAY_HTTP_URL}/pair-sessions/${pairing!.pairingId}/poll`, {
          signal: ac.signal,
        });
      } catch {
        return;
      }
      if (!res.ok) return;
      let body:
        | { pending: true }
        | { expired: true }
        | {
            mobilePub: string;
            encryptedPayload: string;
            iv: string;
            tag: string;
          };
      try {
        body = await res.json();
      } catch {
        return;
      }

      if ('pending' in body && body.pending) return;
      if ('expired' in body && body.expired) {
        if (unmounted) return;
        ac.abort();
        setStatus('expired');
        return;
      }
      if (!('mobilePub' in body)) return;

      // Stage 2 — completion. We have the ciphertext, so this row
      // belongs to us; stop polling and run the decrypt chain. Any
      // failure from here (corrupt blob, bad ECDH peer pubkey,
      // saveIdentity rejection) is fatal — the user lands in the
      // error state with a Retry button rather than seeing a stale
      // QR.
      ac.abort();
      try {
        const mobilePubBytes = base64ToBytes(body.mobilePub);
        const ciphertext = base64ToBytes(body.encryptedPayload);
        const iv = base64ToBytes(body.iv);
        const tag = base64ToBytes(body.tag);

        const mobilePub = await ecdhP256.importPublicKey(mobilePubBytes);
        const sharedSecret = await ecdhP256.sharedSecret(pairing!.webPrivKey, mobilePub);
        const aesKey = await hkdfSha256(
          sharedSecret,
          utf8ToBytes(pairing!.pairingId),
          PAIRING_INFO,
          32,
        );
        const plaintext = await aesGcmDecrypt(aesKey, ciphertext, iv, tag);
        const decoded = JSON.parse(bytesToUtf8(plaintext)) as {
          teamKey: string;
          companyId: string;
          technicianId: string;
          name: string;
          isAdmin: boolean;
          role: 'creator' | 'admin' | 'member';
          companyToken: string;
          expiresAt: number;
        };

        // Build the identity row + a locally-generated terminalId
        // for the web_terminals registry. UUID-style hex of 16 bytes
        // is consistent with the rest of the relay's id format.
        const terminalId = bytesToHex(crypto.getRandomValues(new Uint8Array(16)));
        const identity: WebIdentity = {
          teamKey: decoded.teamKey,
          companyId: decoded.companyId,
          technicianId: decoded.technicianId,
          name: decoded.name,
          isAdmin: decoded.isAdmin,
          role: decoded.role,
          companyToken: decoded.companyToken,
          expiresAt: decoded.expiresAt,
          pairedAt: Date.now(),
          terminalId,
        };
        await saveIdentity(identity);

        // Best-effort follow-ups (DELETE consumed row, POST to
        // web_terminals registry). Failures are non-fatal — the
        // server-side TTL sweeps the pair row anyway, and the
        // terminal row is re-created on next pair attempt.
        fetch(`${RELAY_HTTP_URL}/pair-sessions/${pairing!.pairingId}`, {
          method: 'DELETE',
        }).catch(() => {});

        fetch(`${RELAY_HTTP_URL}/web-terminals`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-company-token': identity.companyToken,
            'x-worker-id': identity.technicianId,
          },
          body: JSON.stringify({
            terminalId,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          }),
        }).catch(() => {});

        if (unmounted) return;
        setStatus('paired');
        onPaired(identity);
      } catch (e) {
        if (unmounted) return;
        setError(e instanceof Error ? e.message : 'pair completion failed');
        setStatus('error');
      }
    }

    const intervalId = window.setInterval(() => {
      void pollOnce();
    }, POLL_INTERVAL_MS);
    // Fire once immediately so a fast scanner doesn't wait the full
    // 2s before the first poll.
    void pollOnce();

    // Local 5-minute regen — fall back to fresh pairingId BEFORE the
    // server's TTL elapses so the user never sees the "expired" flash
    // when the network is healthy.
    const ttlTimer = window.setTimeout(() => {
      if (unmounted) return;
      ac.abort();
      window.clearInterval(intervalId);
      setStatus('expired');
    }, PAIRING_TTL_MS);

    return () => {
      unmounted = true;
      ac.abort();
      window.clearInterval(intervalId);
      window.clearTimeout(ttlTimer);
    };
  }, [status, pairing, onPaired]);

  // Auto-regen on expired — keep the surface responsive without
  // requiring a user click.
  useEffect(() => {
    if (status !== 'expired') return;
    const t = window.setTimeout(() => setRegenTrigger((n) => n + 1), 600);
    return () => window.clearTimeout(t);
  }, [status]);

  const code = useMemo(
    () => (pairing ? pairing.pairingId.slice(0, 6).toUpperCase() : ''),
    [pairing],
  );

  return (
    <div
      className="pair-shell"
      data-testid="pair-screen"
      data-status={status}
      // Spec 200 §10 Nivel 1 contract: Playwright tests read the
      // pairing URI from this attribute instead of decoding the QR
      // pixmap. Stays attached only while a payload exists; absent
      // during generating/expired transitions so a stale value
      // can't be parsed.
      data-pair-uri={pairing?.pairUri ?? undefined}
    >
      {/* Split-screen layout (≥900 px): left hero sells the brand
          + E2EE story, right side hosts the active pair flow. Below
          900 px the grid collapses to a single column with the hero
          stacked on top so a phone-sized window still works. */}
      <section className="pair-hero" aria-label="Cuadrilla — emparejamiento">
        <div className="pair-hero-inner">
          {/* Top — brand */}
          <div className="pair-hero-top">
            <div className="pair-brand-block">
              <BrandMark size={28} ariaLabel="Cuadrilla" tone="security" />
              <span className="pair-brand">{t('pair.brandName')}</span>
            </div>
          </div>

          {/* Center — headline + preview card */}
          <div className="pair-hero-center">
            <h1 className="pair-hero-title">{t('pair.heroTitle')}</h1>

            {/* Decorative preview — sample work card */}
            <div className="pair-preview-card" aria-hidden="true">
              <div className="pair-preview-header">
                <div className="pair-preview-dot pair-preview-dot--done" />
                <span className="pair-preview-title">Revisión caldera · Calle Mayor 12</span>
                <span className="pair-preview-badge pair-preview-badge--done">Completado</span>
              </div>
              <div className="pair-preview-row">
                <div className="pair-preview-avatar">JM</div>
                <div className="pair-preview-meta">
                  <span>José Martínez</span>
                  <span className="pair-preview-time">hace 2h · PDF enviado ✓</span>
                </div>
              </div>
              <div className="pair-preview-footer">
                <div className="pair-preview-dot pair-preview-dot--pending" />
                <span className="pair-preview-title" style={{ opacity: 0.7 }}>Fuga tubería · Av. Constitución 4</span>
                <span className="pair-preview-badge pair-preview-badge--pending">Pendiente</span>
              </div>
            </div>
          </div>

          {/* Bottom — feature bullets */}
          <div className="pair-hero-bottom">
            <ul className="pair-hero-bullets">
              <li>
                <ShieldCheck size={18} strokeWidth={2} aria-hidden="true" />
                <span><strong>{t('pair.bullet1Title')}</strong> {t('pair.bullet1Body')}</span>
              </li>
              <li>
                <Smartphone size={18} strokeWidth={2} aria-hidden="true" />
                <span><strong>{t('pair.bullet2Title')}</strong> {t('pair.bullet2Body')}</span>
              </li>
              <li>
                <Monitor size={18} strokeWidth={2} aria-hidden="true" />
                <span><strong>{t('pair.bullet3Title')}</strong> {t('pair.bullet3Body')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <main className="pair-main">
        <h2 className="pair-title">{t('pair.mainTitle')}</h2>
        <ol className="pair-steps">
          <li>{t('pair.step1')}</li>
          <li>
            <Trans i18nKey="pair.step2" components={[<span />, <strong />]}>
              Entra en <strong>Ajustes → «Cuadrilla web»</strong>.
            </Trans>
          </li>
          <li>{t('pair.step3')}</li>
        </ol>

        {(status === 'generating' || status === 'expired') && (
          <div className="pair-spinner" data-testid="pair-spinner" aria-live="polite">
            <RefreshCw size={32} strokeWidth={2} className="pair-spin" aria-hidden="true" />
            <p className="pair-spinner-msg">
              {status === 'expired' ? t('pair.expired') : t('pair.generating')}
            </p>
          </div>
        )}

        {status === 'waiting' && pairing && (
          <>
            <div className="pair-qr-frame" data-testid="pair-qr-frame" data-pulsing="true">
              <img
                src={pairing.qrDataUrl}
                alt={t('pair.qrAlt')}
                width={240}
                height={240}
                data-testid="pair-qr-image"
              />
            </div>
            <div className="pair-code-box" data-testid="pair-code-box">
              <span className="pair-code-label">{t('pair.codeLabel')}</span>
              <span className="pair-code-value" data-testid="pair-code-value">
                {code}
              </span>
              <span className="pair-code-hint">{t('pair.codeHint')}</span>
            </div>
          </>
        )}

        {status === 'paired' && (
          <div className="pair-success" data-testid="pair-success">
            <ShieldCheck size={48} strokeWidth={2} aria-hidden="true" />
            <p className="pair-success-msg">{t('pair.paired')}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="pair-error" data-testid="pair-error">
            <p>{error ?? t('pair.error')}</p>
            <button
              type="button"
              className="pair-cta"
              onClick={() => setRegenTrigger((n) => n + 1)}
              data-testid="pair-retry"
            >
              {t('pair.retry')}
            </button>
          </div>
        )}

        <div className="pair-caveat" data-testid="pair-screenshot-caveat">
          <Info size={20} strokeWidth={2} aria-hidden="true" />
          <p>
            <strong>{t('pair.caveatTitle')}</strong> {t('pair.caveatBody')}
          </p>
        </div>
      </main>
    </div>
  );
}
